import { useMemo, useRef } from 'react';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import useNavigateToArtwork from '@/hooks/useNavigateToArtwork';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';

import "./styles/VirtualList.css";

function getArtworkAspectRatio(artwork) {
  const aspectRatio = Number(
    artwork?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio
  );

  return Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
}

export default function VirtualList({ items, cacheKey }) {
  const artworks = items.artlist_artworks ?? [];
  const navigateToArtwork = useNavigateToArtwork(
    cacheKey,
    { data: artworks },
    '',
    { type: 'artlist', label: items.artlist_title ?? '' }
  );
  const listRef = useRef();
  const artworkLayoutKey = useMemo(
    () => artworks.map((artwork) => `${artwork._id}:${getArtworkAspectRatio(artwork)}`).join('|'),
    [artworks]
  );

  return (
    <WindowScroller>
      {({ height, isScrolling, onChildScroll, scrollTop }) => (
        <AutoSizer disableHeight>
          {({ width }) => {
            const listWidth = Math.max(width, 1);
            const rowHeight = ({ index }) => {
              const imageHeight = Math.min(
                listWidth / getArtworkAspectRatio(artworks[index]),
                height * 0.9
              );
              const spacer = listWidth <= 650 ? 11 : 20;

              return imageHeight + spacer;
            };

            return (
              <List
                key={`${artworkLayoutKey}:${listWidth}`}
                autoHeight
                width={listWidth}
                height={height}
                rowCount={artworks.length}
                rowHeight={rowHeight}
                overscanRowCount={5}
                isScrolling={isScrolling}
                onScroll={onChildScroll}
                scrollTop={scrollTop}
                estimatedRowSize={height}
                ref={listRef}
                rowRenderer={({ index, key, style }) => {
                  const artwork = artworks[index];
                  const aspectRatio = getArtworkAspectRatio(artwork);

                  return (
                    <div key={key} style={style}>
                      <ArtworkImage
                        image={artwork?.artwork_media?.artwork_main_picture?.image_id}
                        imageClassName="artlist-content__artwork-image"
                        imageTemplateClassName="artlist-content__artwork-image-template"
                        onClick={() => navigateToArtwork(artwork?.slug)}
                        style={{ aspectRatio }}
                        artworkData={artwork}
                      />
                    </div>
                  );
                }}
              />
            );
          }}
        </AutoSizer>
      )}
    </WindowScroller>
  );
}
